import { useEffect, useRef, useState, useCallback } from 'react';
import Select2, { components } from 'react-select';
import { Api } from '../../../services';
import { selectBaseStyles } from '@components';
import './MapSelectSearchInput.scss';

const MenuList = (props) => {
  const { children } = props;
  const elRef = useRef(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    const onScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        props.selectProps?.onLoadMore?.();
      }
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [props.selectProps]);

  return (
    <components.MenuList {...props} innerRef={elRef}>
      {children}
    </components.MenuList>
  );
};

const unwrap = (res) => res?.data ?? res ?? {};

const toRelative = (next) => {
  if (!next) return null;
  return next.replace(/^\/?api\/?/i, '').replace(/^\//, '');
};

const buildOptions = (maps) =>
  (maps ?? [])
    .map((m) => ({
      value: m.mapId ?? m.MapId ?? m.id ?? m.Id,
      label: m.name ?? m.Name,
    }))
    .filter((o) => o.value != null && o.label);

const buildFirstPageUrl = (search: string) => {
  const params = new URLSearchParams();
  params.set('pageSize', '50');
  params.set('page', '1');
  params.set('search', search ?? '');
  return `v2/maps?${params.toString()}`;
};

const MapSelectSearchInput = ({ selectedOption, onMapChange, placeholder = 'Select a Map' }) => {
  const didInitRef = useRef(false);

  const [mapOptions, setMapOptions] = useState<any[]>([]);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const fetchPage = useCallback(
    async (url: string, { append }: { append: boolean }) => {
      if (!url || isLoading) return;

      setIsLoading(true);
      try {
        const res = await Api.get(url);
        const payload = unwrap(res);

        const pageItems = payload.value ?? payload.items ?? [];
        const newOptions = buildOptions(pageItems);

        setMapOptions((prev) => {
          const merged = append ? [...prev, ...newOptions] : newOptions;

          const seen = new Set();
          return merged.filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)));
        });

        setNextUrl(payload.next ? toRelative(payload.next) : null);
      } catch (e) {
        console.error('Error fetching maps:', e);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading],
  );

  // preload page 1 on mount (empty search)
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    fetchPage(buildFirstPageUrl(''), { append: false });
  }, [fetchPage]);

  const onLoadMore = () => {
    if (nextUrl) fetchPage(nextUrl, { append: true });
  };

  const handleChange = (opt: any, meta: any) => {
    // X clicked
    if (meta?.action === 'clear') {
      setInputValue('');
      setNextUrl(null);
      fetchPage(buildFirstPageUrl(''), { append: false }); // EMPTY search
      onMapChange?.(null, null);
      return;
    }

    // Normal select
    setInputValue('');
    onMapChange?.(opt?.value ?? null, opt?.label ?? null);
  };

  const handleInputChange = (val: string, meta: any) => {
    // react-select sometimes reports X clear here too
    if (meta?.action === 'clear') {
      setInputValue('');
      setNextUrl(null);
      fetchPage(buildFirstPageUrl(''), { append: false }); // EMPTY search
      return '';
    }

    if (meta?.action !== 'input-change') return val;

    setInputValue(val);
    setNextUrl(null);
    fetchPage(buildFirstPageUrl(val), { append: false });

    return val;
  };

  return (
    <Select2
      className="w-100"
      isClearable
      options={mapOptions}
      value={selectedOption ?? null}
      placeholder={placeholder}
      onChange={handleChange}
      isLoading={isLoading}
      onLoadMore={onLoadMore}
      components={{ MenuList }}
      maxMenuHeight={260}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      formatOptionLabel={(option, { context }) => {
        if (context === 'menu') {
          return (
            <div>
              <div>{option.label}</div>
              <div id="map-id-value">Map ID: {option.value}</div>
            </div>
          );
        }
        return option.label;
      }}
      styles={selectBaseStyles}
    />
  );
};

export default MapSelectSearchInput;
